import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  SimpleMessageModalComponent
} from '../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { LiveReaderRefusalModalComponent } from './live-reader-refusal-modal/live-reader-refusal-modal.component';


// 'live' only when a recheck got the unconfirmed refusal again; any outcome the
// host handled itself (including opening the prompt) is 'other'.
export type LiveReaderCheckOutcome = 'cleared' | 'live' | 'other';

export interface LiveReaderRefusal {
  message: string;
  // Re-sends the refused request without the bypass flag (PRO-1582).
  checkAgain: () => Promise<LiveReaderCheckOutcome>;
  // On the refusal, not the alert, so every alert rendering it (the prompt's and
  // the host's inline one after Cancel) blocks a second request while one is in flight.
  checking: boolean;
  lastCheckAt?: Date;
}

export interface LiveReaderActionOptions {
  bypass?: boolean;
  // A recheck keeps the host's inline alert it was started from on screen until it settles.
  recheck?: boolean;
}


// Refusals for a second payment while a cancelled MX Merchant reader payment
// may still be live on the terminal (PRO-1582). Matched on the first sentence.
const LIVE_READER_UNCONFIRMED_PREFIX = 'The payment appears to still be live on the terminal.';
const LIVE_READER_CAPTURED_PREFIX = 'The customer already paid this sale on the card reader.';

// Payment endpoints return a bare JSON string (and a non-JSON error body also
// arrives as the raw string); Staff Site sale endpoints return `useful_error_message`.
export function getErrorMessageText(reason: any): string {
  const error = reason?.error;
  if (typeof error === 'string') { return error; }
  if (typeof error?.useful_error_message === 'string') { return error.useful_error_message; }
  return '';
}

export function isLiveReaderUnconfirmedError(reason: any): boolean {
  if (reason?.status !== 400) { return false; }
  return (
    reason.error?.live_reader_payment_unconfirmed === true ||
    getErrorMessageText(reason).startsWith(LIVE_READER_UNCONFIRMED_PREFIX)
  );
}

export function isLiveReaderCapturedError(reason: any): boolean {
  if (reason?.status !== 400) { return false; }
  return (
    reason.error?.live_reader_payment_captured === true ||
    getErrorMessageText(reason).startsWith(LIVE_READER_CAPTURED_PREFIX)
  );
}

export function openProceedAnywayModal(modalService: NgbModal,
                                       refusal: LiveReaderRefusal,
                                       onProceed: () => void,
                                       onDismiss?: () => void) {
  const modalRef = modalService.open(LiveReaderRefusalModalComponent, { backdrop: 'static' });
  modalRef.componentInstance.refusal = refusal;
  modalRef.componentInstance.onProceed = onProceed;
  modalRef.componentInstance.onDismiss = onDismiss ?? null;
}


// The captured refusal means the payment just completed; hosts close as on success.
export function openAlreadyPaidModal(modalService: NgbModal, messageText: string) {
  const modalRef = modalService.open(SimpleMessageModalComponent);
  modalRef.componentInstance.headerText = 'Already Paid on Card Reader';
  modalRef.componentInstance.messageText = messageText;
  modalRef.componentInstance.messageAlertClass = 'alert-info';
}
